import { EyeOutlined } from "@ant-design/icons"
import { App, Button } from "antd"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../components/ActionTooltip.tsx"
import { FilePreviewModal } from "../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { unavailableProps } from "../../../utils/unavailable-control.ts"
import { getInvoice } from "../../Project/api/project-invoices.services.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import { useInvoiceDocumentPreview } from "../hooks/use-invoice-document-preview.ts"
import { KeepEnterInside } from "./KeepEnterInside.tsx"

interface ViewInvoiceButtonProps {
  /** The invoice picked beside this button; nothing to view without one. */
  invoiceId?: string | null
  size?: "small" | "middle"
}

/** Opens the invoice document itself: the issued PDF, or the uploaded file. */
export function ViewInvoiceButton({ invoiceId, size }: ViewInvoiceButtonProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [fetching, setFetching] = useState(false)
  const reportFailure = useCallback(
    () => void message.error(t("finance.viewInvoice.failed")),
    [message, t]
  )
  const preview = useInvoiceDocumentPreview(reportFailure)

  const handleView = async () => {
    if (!invoiceId) return
    setFetching(true)
    try {
      // The record names the document (its number, or the uploaded file's name).
      const response = await getInvoice(invoiceId)
      const record = response?.ok
        ? unwrapEnvelope<ProjectInvoice>(response.data)
        : null
      await preview.open(
        record?.id ? record : ({ id: invoiceId } as ProjectInvoice)
      )
    } catch {
      reportFailure()
    } finally {
      setFetching(false)
    }
  }

  const label = t("finance.viewInvoice.tooltip")

  return (
    <>
      <ActionTooltip
        title={invoiceId ? label : t("finance.viewInvoice.pickFirst")}
      >
        <Button
          size={size}
          icon={<EyeOutlined />}
          aria-label={label}
          loading={fetching || preview.loading}
          {...unavailableProps(!invoiceId, () => void handleView())}
        />
      </ActionTooltip>
      <KeepEnterInside>
        <FilePreviewModal
          visible={Boolean(preview.doc)}
          doc={preview.doc}
          onClose={preview.close}
        />
      </KeepEnterInside>
    </>
  )
}
